// ═══════════════════════════════════════════════════════════
// Upload Tokens — secure, time-limited, no-auth upload links
//
// Staff generate a token per request. The URL /upload/{token}
// lets department staff upload files without a system account.
// Tokens are validated anonymously via the v2proxy.
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';

const F = FIELDS.uploadTokens;

export interface UploadToken {
  recordId: number;
  requestId: number;
  token: string;
  label: string;
  expiresAt: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
}

function rowToToken(row: Record<string, unknown>): UploadToken {
  return {
    recordId:  row['record_id'] as number,
    requestId: Number(row[F.requestId] ?? 0),
    token:     String(row[F.token] ?? ''),
    label:     String(row[F.label] ?? ''),
    expiresAt: String(row[F.expiresAt] ?? ''),
    isActive:  row[F.isActive] === true || row[F.isActive] === 'true',
    createdBy: String(row[F.createdBy] ?? ''),
    createdAt: String(row[F.createdAt] ?? ''),
  };
}

// ── Queries ──────────────────────────────────────────────

export async function listTokensForRequest(requestId: number): Promise<UploadToken[]> {
  const { records } = await queryRecords(TABLES.uploadTokens, {
    filters: [{ field: F.requestId, operator: 'eq', value: requestId }],
    sort:    { field: F.createdAt, direction: 'desc' },
    limit:   50,
  });
  return (records as Record<string, unknown>[]).map(rowToToken);
}

/**
 * Validate an upload token — called anonymously from the public upload page.
 * Returns the token if valid and not expired, null otherwise.
 */
export async function validateToken(token: string): Promise<UploadToken | null> {
  const { records } = await queryRecords(TABLES.uploadTokens, {
    filters: [
      { field: F.token,    operator: 'eq', value: token },
      { field: F.isActive, operator: 'eq', value: true  },
    ],
    limit: 1,
  }, { anonymous: true });

  if (!records.length) return null;

  const t = rowToToken(records[0] as Record<string, unknown>);

  // Check expiry
  if (new Date(t.expiresAt) < new Date()) return null;

  return t;
}

// ── Mutations ─────────────────────────────────────────────

export interface CreateTokenInput {
  requestId: number;
  label: string;
  expiryDays: number;
  createdBy: string;
}

export async function createUploadToken(input: CreateTokenInput): Promise<UploadToken> {
  const token = crypto.randomUUID();
  const now   = new Date();
  const expires = new Date(now.getTime() + input.expiryDays * 86_400_000);

  const row = await createRecord(TABLES.uploadTokens, {
    [F.requestId]: input.requestId,
    [F.token]:     token,
    [F.label]:     input.label,
    [F.expiresAt]: expires.toISOString(),
    [F.isActive]:  true,
    [F.createdBy]: input.createdBy,
    [F.createdAt]: now.toISOString(),
  });

  return rowToToken(row as Record<string, unknown>);
}

export async function revokeToken(recordId: number): Promise<void> {
  await updateRecord(TABLES.uploadTokens, recordId, {
    [F.isActive]: false,
  });
}
